/**
 * What a fetched chat history page's ABSENCES prove — the one rule that decides
 * how far a reconciliation may delete.
 *
 * This lives on its own because it has to hold in three places at once:
 *
 *  - `store/chatStore.js#reconcileMessages` — the in-memory buffer, i.e. what
 *    the user is looking at right now.
 *  - `services/chatHistoryCache.js#cacheHistoryPage` — the on-disk cache, i.e.
 *    what the next launch will show.
 *  - `src-tauri/src/sync/chat_cache.rs#PageSpan` — the same rule again, in Rust,
 *    because that is where the DELETE statement is. That copy cannot import
 *    this one; it is kept in step by having the same tests on both sides.
 *
 * The first two used to be "both happening to be right" rather than the same
 * rule: `cacheHistoryPage` refused a non-array structurally while
 * `reconcileMessages` coerced one into an authoritative EMPTY page and deleted
 * the whole channel. Both now call [`isServerPage`].
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 * A page speaks for a closed `[lo, hi]` range of `server_seq`. A confirmed
 * message inside that range whose uid the page omits was unsent server-side
 * (`Chat.list_messages` filters `is_nil(m.deleted_at)`, so a deleted message is
 * absent rather than flagged, and a device that was closed when it happened
 * will never receive any other evidence).
 *
 *  - **Interior page** (fetched WITH `before_seq`, i.e. scrollback): cut off at
 *    both ends, so it speaks only for `[min, max]` of its own contents, and an
 *    empty one speaks for nothing at all — "nothing older than my cursor" is
 *    what the top of every finite conversation looks like.
 *  - **Tip page** (fetched with no `before_seq`): cut off only at the bottom by
 *    `limit`, so nothing can hide above its oldest row. Its range therefore
 *    extends past its own max, up to `tipAtRequest`.
 *
 * ── Why the tip range is bounded and not `[min, ∞)` ─────────────────────────
 * Because the page is built by the server at one instant and applied here at a
 * later one. A message that arrives in between is ABSENT from the page for the
 * only innocent reason there is: it did not exist yet. An unbounded tip range
 * reads that absence as an unsend and deletes it.
 *
 * That is not a rare interleaving — it is the send path. The user types while
 * the cold-open fetch is in flight; the echo arrives carrying a `server_seq`;
 * `ChatMessageView` writes that seq onto the optimistic row, which removes the
 * no-`server_seq` exemption that would otherwise have protected it. So the
 * message deleted is the one the user just sent.
 *
 * `tipAtRequest` is the newest `server_seq` THIS DEVICE knew about at the
 * moment it issued the request. Everything the page can legitimately speak
 * about is at or below it: a message the device already knew about was already
 * in the server's table when the page was built, so if the page omits it, it
 * was unsent. Anything above it was created after the request went out and the
 * page is silent about it for a reason that is not deletion.
 *
 * The range is `[min, max(page_max, tipAtRequest)]` rather than
 * `[min, tipAtRequest]` because a page may legitimately contain rows above the
 * known tip (messages that arrived before the server built it), and deletions
 * strictly inside a page's own contents are always justified.
 *
 * An UNSTATED `tipAtRequest` means "this caller does not know what it knew",
 * whose harmless reading is the page's own span — the same conservative default
 * `reachesTip` itself takes. Under-deleting costs one cache miss; over-deleting
 * costs the user a message.
 *
 * ── Why the bound alone is not enough: `exemptUids` ─────────────────────────
 * The bound is a PROXY for the real question, which is "did this message exist
 * when I asked". It is a good proxy only if `tipAtRequest` is genuinely the tip
 * as of the request. On the cold-open path it is not quite: it is
 * `lastSeqOf(await diskRead)`, i.e. the tip the disk read SAW. That read is
 * dispatched before the fetch but EXECUTES later, and
 * `chatHistoryCache.cacheMessage` writes live arrivals into the same table in
 * between — so the read can return a tip that already includes a message the
 * page could not have known about, and the bound then covers exactly the
 * message it exists to protect.
 *
 * Two earlier attempts to capture the tip EARLIER both failed structurally: the
 * device's knowledge on a cold open simply is not known until the disk read
 * resolves. So the condition is stated directly instead. `exemptUids` are the
 * uids that arrived live between the request being issued and its page being
 * applied (`chatStore.liveArrivalsByChannel`), and a page built before a
 * message existed cannot speak for it — the same reasoning that exempts
 * optimistic messages, one step later in their life.
 *
 * The exemption is a uid set rather than a seq, so it is applied where uids are
 * compared — `chatStore.reconcileMessages`'s survivor filter and Rust's
 * `keep` set — not inside `pageDeletionSpan`, which knows only about ranges.
 * Both layers need it: `cacheMessage` has already written the arrival's row to
 * disk, so the disk is in exactly the same position as the buffer.
 */

/**
 * Is this an actual server page, as opposed to the absence of an answer?
 *
 * `chatService.getMessages` returns `res.data.messages`, so a 200 whose body
 * lacks that key resolves to `undefined` — a SUCCESSFUL promise carrying no
 * page. An empty array and a non-array are opposite facts: `[]` from a tip
 * fetch is the strongest deletion signal this system produces ("the channel has
 * nothing visible"), while a non-array is a fetch that did not answer and must
 * delete nothing and verify nothing.
 */
export function isServerPage(msgs) {
  return Array.isArray(msgs);
}

/**
 * The closed `[lo, hi]` `server_seq` range this page's absences are evidence
 * about, or `null` for "this page proves no deletion".
 *
 * ── Two known divergences from the Rust copy, neither reachable today ───────
 * Recorded because "the two layers apply one rule" is this module's whole
 * claim, and both of these are places where they agree by coincidence rather
 * than by construction.
 *
 * **1. The empty-tip-page gate at `knownTip > 0`.** This side returns `null`
 * (delete nothing) when an empty tip page arrives with `knownTip === 0`; Rust
 * has no such gate and returns `(i64::MIN, 0)`, a range that includes
 * `server_seq = 0`. So with `known_tip = 0` an empty tip page would delete a
 * disk row at seq 0 that the buffer keeps. Unreachable with real sequence
 * numbers — `chat_messages.server_seq` is a Postgres `bigserial` and starts at
 * 1, so no row ever has seq 0 — but if that ever stops being true, this gate is
 * the thing to port, not the thing to remove.
 *
 * **2. `m == null` tolerance is one-sided.** This function skips null entries in
 * a page, but `chatStore.dedupeSort` reads `m.uid` unguarded, so a page
 * containing `null` throws before any of this matters. See `dedupeSort`'s own
 * note: the tolerance here is a promise the store does not keep.
 *
 * @param {Array} page   a real server page (see {@link isServerPage})
 * @param {{reachesTip?: boolean, tipAtRequest?: number}} opts
 */
export function pageDeletionSpan(page, { reachesTip = false, tipAtRequest = 0 } = {}) {
  let lo = null;
  let hi = null;
  for (const m of page) {
    if (m == null || m.server_seq == null) continue;
    if (lo === null || m.server_seq < lo) lo = m.server_seq;
    if (hi === null || m.server_seq > hi) hi = m.server_seq;
  }
  const knownTip = Number.isFinite(tipAtRequest) ? tipAtRequest : 0;
  // The viewer's plan limits how far back the SERVER will serve (Free: 90
  // days). Absence from such a page is not evidence of anything: the messages
  // are still there, this account may simply not read them.
  const windowed = Number.isFinite(page?.historyDays) && page.historyDays > 0;

  if (!reachesTip) {
    // Interior: its own closed span, and nothing at all when it is empty.
    return lo === null ? null : { lo, hi };
  }
  if (lo === null) {
    // An empty tip page: the channel has no visible messages at all, so
    // everything the device knew about is gone. There is no page max to fall
    // back on, which is exactly why the known tip has to carry the bound.
    //
    // UNLESS the page was windowed by the plan. A conversation whose last
    // message is older than the window returns an empty tip page while every
    // message is still on the server, and deleting the device's cache there
    // would destroy the one copy the user can still read offline.
    if (windowed) return null;
    return knownTip > 0 ? { lo: Number.NEGATIVE_INFINITY, hi: knownTip } : null;
  }
  return { lo, hi: Math.max(hi, knownTip) };
}
